/**
 * API Reference Tab - Admin Dashboard
 * Static documentation for Accelpix Greeks API fields
 */

import React from 'react';
import { BookOpen } from 'lucide-react';

const APIReferenceTab = () => {
  // Static API definitions - hardcoded for documentation purposes
  const apiDefinitions = [
    { id: 1, key: "ticker", desc: "Instrument Symbol (e.g., NIFTY25JUL25000CE)" },
    { id: 2, key: "iv", desc: "Implied Volatility" },
    { id: 3, key: "delta", desc: "Delta (Δ) - Directional Risk" },
    { id: 4, key: "gamma", desc: "Gamma (Γ) - Rate of Change of Delta" },
    { id: 5, key: "theta", desc: "Theta (Θ) - Time Decay" },
    { id: 6, key: "vega", desc: "Vega (ν) - Volatility Sensitivity" },
    { id: 7, key: "rho", desc: "Rho (ρ) - Interest Rate Sensitivity" },
    { id: 8, key: "vanna", desc: "Vanna - Sensitivity of Delta to Volatility" },
    { id: 9, key: "charm", desc: "Charm - Sensitivity of Delta to Time Decay" },
    { id: 10, key: "speed", desc: "Speed - Rate of Change of Gamma" },
    { id: 11, key: "zomma", desc: "Zomma - Sensitivity of Gamma to Volatility" },
    { id: 12, key: "color", desc: "Color - Sensitivity of Gamma to Time" },
    { id: 13, key: "volga", desc: "Volga - Sensitivity of Vega to Volatility" },
    { id: 14, key: "tgr", desc: "Target Gamma Risk (Proprietary)" },
    { id: 15, key: "dtr", desc: "Directional Theta Risk (Proprietary)" },
    { id: 16, key: "tv", desc: "Theoretical Option Value (Fair Price)" },
    { id: 17, key: "timestamp", desc: "Epoch Time (in milliseconds)" }
  ];

  // Group definitions by category
  const categories = {
    "Core Identifiers": [1, 17],
    "First-Order Greeks": [2, 3, 5, 6, 7],
    "Second-Order Greeks": [4, 8, 9, 10, 11, 12, 13],
    "Proprietary Metrics": [14, 15, 16],
  };

  return (
    <div className="space-y-6" data-testid="api-reference-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-accent" />
          API Data Dictionary
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Reference guide for Accelpix Greeks API data fields. This is a read-only documentation page.
        </p>
      </div>

      {/* Quick Reference Card */}
      <div className="panel p-4 bg-accent/5 border-accent/20">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-accent/20 rounded-lg">
            <BookOpen className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Greeks API Integration</h3>
            <p className="text-sm text-muted-foreground mt-1">
              These fields are returned by the Accelpix Greeks WebSocket API for each option contract.
              The system uses these values for GEX calculations and the God Score engine.
            </p>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-surface/50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-12">
                  #
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-40">
                  Key
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">
                  Category
                </th>
              </tr>
            </thead>
            <tbody>
              {apiDefinitions.map((item, index) => {
                // Determine category
                let category = "Other";
                let categoryColor = "bg-gray-500/20 text-gray-400";
                
                if (categories["Core Identifiers"].includes(item.id)) {
                  category = "Core";
                  categoryColor = "bg-blue-500/20 text-blue-400";
                } else if (categories["First-Order Greeks"].includes(item.id)) {
                  category = "1st Order";
                  categoryColor = "bg-green-500/20 text-green-400";
                } else if (categories["Second-Order Greeks"].includes(item.id)) {
                  category = "2nd Order";
                  categoryColor = "bg-purple-500/20 text-purple-400";
                } else if (categories["Proprietary Metrics"].includes(item.id)) {
                  category = "Proprietary";
                  categoryColor = "bg-amber-500/20 text-amber-400";
                }

                return (
                  <tr 
                    key={item.id}
                    className={`border-b border-border/50 ${index % 2 === 0 ? 'bg-surface/30' : ''} hover:bg-surface/50 transition-colors`}
                  >
                    <td className="py-3 px-4">
                      <span className="text-xs text-muted-foreground font-mono">{item.id}</span>
                    </td>
                    <td className="py-3 px-4">
                      <code className="px-2 py-1 bg-accent/10 text-accent text-sm font-mono rounded">
                        {item.key}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-foreground">{item.desc}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${categoryColor}`}>
                        {category}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Legend */}
      <div className="panel p-4">
        <h3 className="font-semibold text-foreground mb-3">Category Legend</h3>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded bg-blue-500/20 text-blue-400">Core</span>
            <span className="text-xs text-muted-foreground">Basic identifiers</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded bg-green-500/20 text-green-400">1st Order</span>
            <span className="text-xs text-muted-foreground">Primary Greeks</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded bg-purple-500/20 text-purple-400">2nd Order</span>
            <span className="text-xs text-muted-foreground">Higher-order Greeks</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded bg-amber-500/20 text-amber-400">Proprietary</span>
            <span className="text-xs text-muted-foreground">Accelpix-specific metrics</span>
          </div>
        </div>
      </div>

      {/* Usage Example */}
      <div className="panel p-4">
        <h3 className="font-semibold text-foreground mb-3">Example WebSocket Response</h3>
        <pre className="bg-surface/50 border border-border rounded p-4 text-xs font-mono text-muted-foreground overflow-x-auto">
{`{
  "ticker": "NIFTY25JUL25000CE",
  "iv": 0.1856,
  "delta": 0.5234,
  "gamma": 0.0012,
  "theta": -15.45,
  "vega": 12.87,
  "rho": 0.0234,
  "vanna": 0.0045,
  "charm": -0.0023,
  "tv": 245.50,
  "timestamp": 1706534400000
}`}
        </pre>
      </div>
    </div>
  );
};

export default APIReferenceTab;
